import React, { useMemo } from 'react';

interface Category {
    categoryName: string;
    categoryId?: string;
    id?: string;
    compatibility?: string[];
    [key: string]: any;
}

interface CompatibleAttributeProps {
    selectedAccessory: any;
    selectedProduct: any;
    selectedCategories: Category[];
    compatibilityAttributes?: string[]; // New prop from parent
}

export default function CompatibleAttribute({ 
    selectedAccessory, 
    selectedProduct, 
    selectedCategories,
    compatibilityAttributes: parentCompatibilityAttrs = [] // Receive from parent
}: CompatibleAttributeProps) {

    
    // Extract compatibility attribute names from selected categories
    const categoryCompatibilityAttributes = useMemo(() => {
        if (!selectedCategories || selectedCategories.length === 0) return [];
        
        const allCompatibilityAttrs = new Set<string>();
        
        selectedCategories.forEach((category) => {
            if (category.compatibility && Array.isArray(category.compatibility)) {
                category.compatibility.forEach((attr) => {
                    if (attr) allCompatibilityAttrs.add(attr);
                });
            }
        });
        
        return Array.from(allCompatibilityAttrs);
    }, [selectedCategories]);

    // Merge both compatibility attributes (from parent and selected categories)
    const allCompatibilityAttributes = useMemo(() => {
        const merged = new Set<string>([
            ...categoryCompatibilityAttributes,
            ...parentCompatibilityAttrs
        ]);
        return Array.from(merged);
    }, [categoryCompatibilityAttributes, parentCompatibilityAttrs]);

    /* console.log("Category compatibility attrs:", categoryCompatibilityAttributes);
    console.log("Parent compatibility attrs:", parentCompatibilityAttrs);
    console.log("All merged compatibility attrs:", allCompatibilityAttributes); */

    // Extract and filter attributes from product
    const productAttributes = useMemo(() => {
        if (!selectedProduct || !selectedProduct.attributes) return [];
        
        let allAttrs = [];
        
        // Check if attributes is an array
        if (Array.isArray(selectedProduct.attributes)) {
            allAttrs = selectedProduct.attributes.map((attr: any) => ({
                name: attr.attributeName || 'N/A',
                value: attr.value || 'N/A',
                unit: attr.unit?.name || ''
            }));
        } else {
            // If it's an object, convert to array
            allAttrs = Object.entries(selectedProduct.attributes).map(([key, value]) => ({
                name: key,
                value: value,
                unit: ''
            }));
        }
        
        // Filter based on ALL compatibility attributes (merged from parent and categories)
        if (allCompatibilityAttributes.length > 0) {
            return allAttrs.filter((attr: any) => 
                allCompatibilityAttributes.includes(attr.name)
            );
        }
        
        return allAttrs;
    }, [selectedProduct, allCompatibilityAttributes]);

    // Extract and filter attributes from accessory
    const accessoryAttributes = useMemo(() => {
        if (!selectedAccessory || !selectedAccessory.attributes) return [];
        
        let allAttrs = [];
        
        // Check if attributes is an array
        if (Array.isArray(selectedAccessory.attributes)) {
            allAttrs = selectedAccessory.attributes.map((attr: any) => ({
                name: attr.attributeName || 'N/A',
                value: attr.value || 'N/A',
                unit: attr.unit?.name || ''
            }));
        } else {
            // If it's an object, convert to array
            allAttrs = Object.entries(selectedAccessory.attributes).map(([key, value]) => ({
                name: key,
                value: value,
                unit: ''
            }));
        }
        
        // Filter based on ALL compatibility attributes (merged from parent and categories)
        if (allCompatibilityAttributes.length > 0) {
            return allAttrs.filter((attr: any) => 
                allCompatibilityAttributes.includes(attr.name)
            );
        }
        
        return allAttrs;
    }, [selectedAccessory, allCompatibilityAttributes]);

    const handleEditProduct = (productId: string | number) => {
        const url = process.env.REACT_APP_BASE_URL + `/product/edit?q=${productId}`;
        window.open(url, '_blank');
    };

    const handleEditAccessory = (productId: string | number) => {
        const url = process.env.REACT_APP_BASE_URL + `/accessory/edit?q=${productId}`;
        window.open(url, '_blank');
    };
    return (
        <div>
            {/* Product Attributes Table */}
            
            {selectedProduct && (
                <div className="card mt-5 mb-5" style={{ position: "relative" }}>
                 
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h3 className="card-title align-items-start flex-column">
                            <span className="card-label fw-bold text-gray-900">Attributes Of Product</span>
                            <span className="mt-1 text-muted fw-semibold fs-6">
                                {selectedProduct.displayName || selectedProduct.productName || 'N/A'}
                            </span>
                        </h3>
                    </div>
                    <div className="card-body" style={{ padding: "10px" }}>
                        {productAttributes.length > 0 ? (
                            <div
                                className="table-responsive"
                                style={{
                                    overflowX: "auto",
                                    overflowY: "hidden",
                                    width: "100%",
                                    maxWidth: "100%",
                                }}
                            >
                                <table
                                    className="table table-bordered mb-0"
                                    style={{
                                        width: "100%",
                                        tableLayout: "auto",
                                        marginBottom: "0"
                                    }}
                                >
                                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                                        <tr>
                                            <th
                                                style={{
                                                    width: "300px",
                                                    minWidth: "300px",
                                                    maxWidth: "300px",
                                                    fontWeight: "bold",
                                                    padding: "12px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    position: "sticky",
                                                    left: "0",
                                                    backgroundColor: "#f8f9fa",
                                                    zIndex: "10"
                                                }}
                                            >
                                                Product Name
                                            </th>
                                            {productAttributes.map((attr: any, index: any) => (
                                                <th
                                                    key={index}
                                                    style={{
                                                        width: "180px",
                                                        minWidth: "180px",
                                                        maxWidth: "180px",
                                                        fontWeight: "bold",
                                                        padding: "12px",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}
                                                    title={attr.name}
                                                >
                                                    {attr.name}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td
                                                style={{
                                                    width: "300px",
                                                    minWidth: "300px",
                                                    maxWidth: "300px",
                                                    padding: "12px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    position: "sticky",
                                                    left: "0",
                                                    backgroundColor: "#fff",
                                                    zIndex: "10"
                                                }}
                                            >

                                                   <i
                                                className="fas fa-edit text-primary me-2"
                                                style={{ cursor: "pointer", fontSize: "14px" }}
                                                title="Edit Product"
                                                onClick={() => handleEditProduct(selectedProduct._id)}
                                            ></i>
                                                <span style={{ fontWeight: "500" }}>
                                                    {selectedProduct.displayName || selectedProduct.productName || 'N/A'}
                                                </span>
                                            </td>
                                            {productAttributes.map((attr: any, index: any) => (
                                                <td
                                                    key={index}
                                                    style={{
                                                        width: "180px",
                                                        minWidth: "180px",
                                                        maxWidth: "180px",
                                                        padding: "12px",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}
                                                    title={String(attr.value)}
                                                >
                                                    {String(attr.value)}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-muted mb-0">No compatible attributes found</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Accessory Attributes Table */}
            {selectedAccessory && (
                <div className="card mb-5" style={{ position: "relative" , marginTop: "30px" }}>
                      <hr />
                    <div className="card-header d-flex justify-content-between align-items-center">
                      
                        <h3 className="card-title align-items-start flex-column">
                            <span className="card-label fw-bold text-gray-900">Attributes Of Accessory</span>
                            <span className="mt-1 text-muted fw-semibold fs-6">
                                {selectedAccessory.accessoryName || selectedAccessory.modelNumber || selectedAccessory.displayName || 'N/A'}
                            </span>
                        </h3>
                    </div>
                    <div className="card-body" style={{ padding: "10px" }}>
                        {accessoryAttributes.length > 0 ? (
                            <div
                                className="table-responsive"
                                style={{
                                    overflowX: "auto",
                                    overflowY: "hidden",
                                    width: "100%",
                                    maxWidth: "100%",
                                }}
                            >
                                <table
                                    className="table table-bordered mb-0"
                                    style={{
                                        width: "100%",
                                        tableLayout: "auto",
                                        marginBottom: "0"
                                    }}
                                >
                                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                                        <tr>
                                            <th
                                                style={{
                                                    width: "300px",
                                                    minWidth: "300px",
                                                    maxWidth: "300px",
                                                    fontWeight: "bold",
                                                    padding: "12px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    position: "sticky",
                                                    left: "0",
                                                    backgroundColor: "#f8f9fa",
                                                    zIndex: "10"
                                                }}
                                            >
                                                Accessory Name
                                            </th>
                                            {accessoryAttributes.map((attr: any, index: any) => (
                                                <th
                                                    key={index}
                                                    style={{
                                                        width: "180px",
                                                        minWidth: "180px",
                                                        maxWidth: "180px",
                                                        fontWeight: "bold",
                                                        padding: "12px",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}
                                                    title={attr.name}
                                                >
                                                    {attr.name}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td
                                                style={{
                                                    width: "300px",
                                                    minWidth: "300px",
                                                    maxWidth: "300px",
                                                    padding: "12px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    position: "sticky",
                                                    left: "0",
                                                    backgroundColor: "#fff",
                                                    zIndex: "10"
                                                }}
                                            >

                                                  <i
                                                className="fas fa-edit text-primary  me-2"
                                                style={{ cursor: "pointer", fontSize: "14px" }}
                                                title="Edit Product"
                                                onClick={() => handleEditAccessory(selectedAccessory._id)}
                                            ></i>
                                                <span style={{ fontWeight: "500" }}>
                                                    {selectedAccessory.accessoryName || selectedAccessory.modelNumber || selectedAccessory.displayName || 'N/A'}
                                                </span>
                                            </td>
                                            {accessoryAttributes.map((attr: any, index: any) => (
                                                <td
                                                    key={index}
                                                    style={{
                                                        width: "180px",
                                                        minWidth: "180px",
                                                        maxWidth: "180px",
                                                        padding: "12px",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}
                                                    title={String(attr.value)}
                                                >
                                                    {String(attr.value)}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-muted mb-0">No compatible attributes found</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Show message if no product or accessory selected */}
           {/*  {!selectedProduct && !selectedAccessory && (
                <div className="card mt-5 mb-5">
                    <div className="card-body text-center py-5">
                        <i className="fas fa-info-circle text-muted fs-2 mb-3"></i>
                        <p className="text-muted mb-0">Please select a product and/or accessory to view attributes</p>
                    </div>
                </div>
            )} */}
        </div>
    );
}